import { Fragment, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  PageHeader,
  StatCard,
  Card,
  SectionHeader,
  Badge,
  Button,
  Input,
  Select,
  Modal,
  Toggle,
  Spinner,
  QueryError,
} from "../components";
import { usePolling } from "../hooks/usePolling";
import { useAuth } from "../hooks/useAuth";
import { apiGet, apiPost, apiPatch } from "../lib/api";
import { useToast } from "../contexts/ToastContext";

interface EmailStats {
  sent24h: number;
  deliveryRate: number;
  bounced: number;
  queued: number;
}

interface RecentEmail {
  id: string;
  to: string;
  subject: string;
  template: string;
  status: "delivered" | "bounced" | "failed" | "sent" | "queued";
  sentAt: string;
  htmlPreview: string;
}

interface EmailTemplate {
  name: string;
  subject: string;
  description: string;
  exampleHtml: string;
}

const STATUS_COLOR: Record<string, "green" | "amber" | "red" | "blue"> = {
  delivered: "green",
  sent: "blue",
  queued: "blue",
  bounced: "amber",
  failed: "red",
};

interface EmailConfig {
  provider: string;
  fromAddress: string;
  rateLimit: number;
  enabled: boolean;
  typePreferences: Record<string, boolean>;
}

interface Tenant {
  id: string;
  name: string;
  email: string;
}

export default function EmailDashboard() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const auth = useAuth();
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [composeOpen, setComposeOpen] = useState(false);
  const [composeRecipient, setComposeRecipient] = useState("all");
  const [composeSubject, setComposeSubject] = useState("");
  const [composeBody, setComposeBody] = useState("");

  const {
    data: stats,
    isLoading: loadingStats,
    error: errorStats,
  } = useQuery<EmailStats>({
    queryKey: ["emails", "stats"],
    queryFn: () => apiGet<EmailStats>("/api/emails/stats"),
  });

  const { data: emails } = useQuery<RecentEmail[]>({
    queryKey: ["emails", "recent"],
    queryFn: () => apiGet<RecentEmail[]>("/api/emails/recent"),
  });

  const { data: templates } = useQuery<EmailTemplate[]>({
    queryKey: ["emails", "templates"],
    queryFn: () => apiGet<EmailTemplate[]>("/api/emails/templates"),
  });

  const { data: tenantsList } = useQuery<Tenant[]>({
    queryKey: ["tenants"],
    queryFn: () => apiGet<{ tenants: Tenant[] }>("/api/tenants").then((d) => d.tenants),
  });

  const { data: emailConfig } = useQuery<EmailConfig>({
    queryKey: ["emails", "config"],
    queryFn: () => apiGet<EmailConfig>("/api/emails/config"),
  });

  const providerConfigured = emailConfig?.enabled === true;
  const canSendTest = providerConfigured && Boolean(auth.email?.trim());
  const providerStatusMessage = providerConfigured
    ? null
    : "Email provider is not configured in this environment. Compose and test-send actions stay disabled until a provider is connected.";

  const updateConfig = useMutation({
    mutationFn: (prefs: Record<string, boolean>) =>
      apiPatch("/api/emails/config", { typePreferences: prefs }),
    onSuccess: () => {
      toast.success("Email preferences updated");
      queryClient.invalidateQueries({ queryKey: ["emails", "config"] });
    },
    onError: (err: Error) => toast.error("Failed to update preferences", err.message),
  });

  usePolling(
    [
      ["emails", "stats"],
      ["emails", "recent"],
    ],
    30_000,
  );

  const sendEmail = useMutation({
    mutationFn: (body: { tenantIds: string[] | "all"; subject: string; body: string }) =>
      apiPost("/api/emails/send", body),
    onSuccess: () => {
      toast.success("Email sent successfully");
      queryClient.invalidateQueries({ queryKey: ["emails", "recent"] });
      queryClient.invalidateQueries({ queryKey: ["emails", "stats"] });
      setComposeOpen(false);
      setComposeRecipient("all");
      setComposeSubject("");
      setComposeBody("");
    },
    onError: (err: Error) => toast.error("Failed to send email", err.message),
  });

  const sendTest = useMutation({
    mutationFn: (template: string) => {
      const target = auth.email?.trim();
      if (!target) {
        throw new Error("No signed-in operator email is available for test delivery");
      }
      return apiPost("/api/emails/send-test", { template, to: target });
    },
    onSuccess: () => {
      toast.success("Test email sent");
      queryClient.invalidateQueries({ queryKey: ["emails", "recent"] });
    },
    onError: (err: Error) => toast.error("Failed to send test email", err.message),
  });

  if (loadingStats && !stats) {
    return (
      <div>
        <PageHeader title="Email Dashboard" subtitle="Email delivery and templates" />
        <div className="flex items-center justify-center py-20">
          <Spinner size={24} />
        </div>
      </div>
    );
  }

  if (errorStats && !stats) {
    return (
      <div>
        <PageHeader title="Email Dashboard" subtitle="Email delivery and templates" />
        <QueryError error={errorStats} queryKeys={[["emails", "stats"]]} />
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <PageHeader title="Email Dashboard" subtitle="Email delivery and templates" />
        <Button
          variant="primary"
          onClick={() => setComposeOpen(true)}
          disabled={!providerConfigured}
        >
          Compose
        </Button>
      </div>

      {providerStatusMessage && (
        <div
          className="mb-6 rounded-lg border px-4 py-3 text-sm"
          style={{
            borderColor: "var(--c-border)",
            backgroundColor: "var(--c-surface)",
            color: "var(--c-text-secondary)",
          }}
        >
          {providerStatusMessage}
        </div>
      )}

      {/* Stat cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard title="Sent (24h)" value={String(stats?.sent24h ?? 0)} />
        <StatCard
          title="Delivery Rate"
          value={`${(stats?.deliveryRate ?? 0).toFixed(1)}%`}
          color="var(--c-success)"
        />
        <StatCard title="Bounced" value={String(stats?.bounced ?? 0)} color="var(--c-danger)" />
        <StatCard title="Queued" value={String(stats?.queued ?? 0)} />
      </div>

      {/* Recent emails */}
      <SectionHeader
        title="Recent Emails"
        subtitle={`${(emails ?? []).length} emails in last 24 hours`}
      />
      <Card padding={false} className="mb-8">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" style={{ color: "var(--c-text)" }}>
            <thead>
              <tr style={{ borderBottom: "1px solid var(--c-border)" }}>
                <th
                  className="text-left text-xs font-medium px-4 py-3"
                  style={{ color: "var(--c-text-secondary)" }}
                >
                  Recipient
                </th>
                <th
                  className="text-left text-xs font-medium px-4 py-3"
                  style={{ color: "var(--c-text-secondary)" }}
                >
                  Subject
                </th>
                <th
                  className="text-left text-xs font-medium px-4 py-3"
                  style={{ color: "var(--c-text-secondary)" }}
                >
                  Template
                </th>
                <th
                  className="text-left text-xs font-medium px-4 py-3"
                  style={{ color: "var(--c-text-secondary)" }}
                >
                  Status
                </th>
                <th
                  className="text-left text-xs font-medium px-4 py-3"
                  style={{ color: "var(--c-text-secondary)" }}
                >
                  Sent
                </th>
              </tr>
            </thead>
            <tbody>
              {(emails ?? []).map((email) => (
                <Fragment key={email.id}>
                  <tr
                    className="cursor-pointer"
                    style={{ borderBottom: "1px solid var(--c-border)" }}
                    onClick={() => setExpandedId(expandedId === email.id ? null : email.id)}
                  >
                    <td className="px-4 py-3 font-mono text-xs">{email.to}</td>
                    <td className="px-4 py-3">{email.subject}</td>
                    <td className="px-4 py-3">
                      <Badge color="blue">{email.template}</Badge>
                    </td>
                    <td className="px-4 py-3">
                      <Badge color={STATUS_COLOR[email.status] ?? "blue"}>{email.status}</Badge>
                    </td>
                    <td className="px-4 py-3 text-xs" style={{ color: "var(--c-text-secondary)" }}>
                      {new Date(email.sentAt).toLocaleString()}
                    </td>
                  </tr>
                  {expandedId === email.id && (
                    <tr style={{ borderBottom: "1px solid var(--c-border)" }}>
                      <td
                        colSpan={5}
                        className="px-4 py-4"
                        style={{ backgroundColor: "var(--c-bg)" }}
                      >
                        <p
                          className="text-xs font-medium mb-2"
                          style={{ color: "var(--c-text-secondary)" }}
                        >
                          HTML Preview
                        </p>
                        <div
                          className="p-4 rounded-lg border text-sm"
                          style={{
                            backgroundColor: "var(--c-surface)",
                            borderColor: "var(--c-border)",
                            color: "var(--c-text)",
                          }}
                        >
                          {/* Preview content rendered as text — sanitized to prevent XSS */}
                          <iframe
                            srcDoc={email.htmlPreview}
                            sandbox=""
                            title="Email preview"
                            style={{ width: "100%", height: 120, border: "none" }}
                          />
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
              {(emails ?? []).length === 0 && (
                <tr>
                  <td
                    colSpan={5}
                    className="px-4 py-8 text-center"
                    style={{ color: "var(--c-text-faint)" }}
                  >
                    No recent emails
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Template gallery */}
      <SectionHeader
        title="Email Templates"
        subtitle={`${(templates ?? []).length} templates available`}
      />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {(templates ?? []).map((tpl) => (
          <Card key={tpl.name}>
            <div className="flex items-start justify-between mb-2">
              <span className="text-sm font-medium" style={{ color: "var(--c-text)" }}>
                {tpl.name}
              </span>
              <Badge color="blue">template</Badge>
            </div>
            <p className="text-xs mb-1" style={{ color: "var(--c-text-secondary)" }}>
              {tpl.subject}
            </p>
            <p className="text-xs mb-3" style={{ color: "var(--c-text-faint)" }}>
              {tpl.description}
            </p>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => sendTest.mutate(tpl.name)}
              disabled={!canSendTest || sendTest.isPending}
            >
              Send Test
            </Button>
          </Card>
        ))}
      </div>

      {/* Email Settings */}
      <div className="mt-8" />
      <SectionHeader
        title="Email Settings"
        subtitle="Provider configuration and notification preferences"
      />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-8">
        <Card>
          <h3 className="text-sm font-semibold mb-3" style={{ color: "var(--c-text)" }}>
            Provider
          </h3>
          <div className="space-y-2 text-xs">
            <div className="flex items-center justify-between">
              <span style={{ color: "var(--c-text-secondary)" }}>Status</span>
              <Badge color={emailConfig?.enabled ? "green" : "red"}>
                {emailConfig?.enabled ? `Connected to ${emailConfig.provider}` : "Not configured"}
              </Badge>
            </div>
            <div className="flex items-center justify-between">
              <span style={{ color: "var(--c-text-secondary)" }}>From Address</span>
              <span className="font-mono" style={{ color: "var(--c-text)" }}>
                {emailConfig?.fromAddress ?? "—"}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span style={{ color: "var(--c-text-secondary)" }}>Rate Limit</span>
              <span style={{ color: "var(--c-text)" }}>{emailConfig?.rateLimit ?? 0}/hr</span>
            </div>
          </div>
        </Card>
        <Card>
          <h3 className="text-sm font-semibold mb-3" style={{ color: "var(--c-text)" }}>
            Notification Preferences
          </h3>
          <div className="space-y-3">
            {emailConfig?.typePreferences &&
              Object.entries(emailConfig.typePreferences).map(([key, enabled]) => (
                <div key={key} className="flex items-center justify-between">
                  <span className="text-xs" style={{ color: "var(--c-text-secondary)" }}>
                    {key.replace(/_/g, " ")}
                  </span>
                  <Toggle
                    checked={enabled}
                    onChange={(checked) => {
                      updateConfig.mutate({
                        ...emailConfig.typePreferences,
                        [key]: checked,
                      });
                    }}
                    size="sm"
                  />
                </div>
              ))}
          </div>
        </Card>
      </div>

      {/* Compose modal */}
      <Modal
        open={composeOpen}
        onClose={() => setComposeOpen(false)}
        title="Compose Email"
        size="md"
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            sendEmail.mutate({
              tenantIds: composeRecipient === "all" ? "all" : [composeRecipient],
              subject: composeSubject,
              body: composeBody,
            });
          }}
          className="space-y-4"
        >
          <Select
            label="Recipient"
            value={composeRecipient}
            onChange={(e) => setComposeRecipient(e.target.value)}
            options={[
              { label: "All tenants", value: "all" },
              ...(tenantsList ?? []).map((t) => ({
                label: `${t.name} (${t.email})`,
                value: t.id,
              })),
            ]}
          />
          <Input
            label="Subject"
            value={composeSubject}
            onChange={(e) => setComposeSubject(e.target.value)}
            placeholder="Email subject line"
          />
          <div>
            <label
              className="block text-xs font-medium mb-1"
              style={{ color: "var(--c-text-secondary)" }}
            >
              Body
            </label>
            <textarea
              value={composeBody}
              onChange={(e) => setComposeBody(e.target.value)}
              placeholder="Email body (plain text)"
              rows={5}
              className="w-full px-3 py-2 rounded-lg border text-sm"
              style={{
                backgroundColor: "var(--c-bg)",
                borderColor: "var(--c-border)",
                color: "var(--c-text)",
              }}
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="secondary" type="button" onClick={() => setComposeOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              type="submit"
              disabled={
                !providerConfigured || !composeSubject || !composeBody || sendEmail.isPending
              }
            >
              Send
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
